import { usePortal } from "../Portal/PortalContext";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  reviewApi,
  dateLabel,
  dueLabel,
  jurisdictionLabel,
  ReviewApiError,
  shortDate,
  topicLabel,
} from "./api";
import ProposalTerms, {
  ProposalTermsData,
  proposalPrice,
} from "./ProposalTerms";
import "./proposals.css";

export default function ReviewProposal({
  review,
  proposals,
  decisions,
  attorney,
  onChanged,
  onAccessError,
  openTarget,
  compact = false,
  savedCard = null,
  payments = [],
  hideBar = false,
  openRequest = 0,
  customerBanner = false,
  parties = "",
}: {
  review: any;
  proposals: any[];
  decisions: any[];
  attorney: boolean;
  savedCard?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  payments?: any[];
  onChanged: () => Promise<void>;
  onAccessError: (error: any) => void;
  openTarget?: string;
  compact?: boolean;
  /** The attorney action bar replaces the summary bar and opens the modal instead. */
  hideBar?: boolean;
  /** Increment to open the current proposal. */
  openRequest?: number;
  customerBanner?: boolean;
  parties?: string;
}) {
  const access=usePortal().access;
  const betaMode = access?.targetedBeta === true;
  const [shown, setShown] = useState<any>(null),
    [editing, setEditing] = useState(false),
    [terms, setTerms] = useState<ProposalTermsData | null>(null),
    [price, setPrice] = useState(""),
    [hours, setHours] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [requestKey, setRequestKey] = useState(() => crypto.randomUUID()),
    [confirmed, setConfirmed] = useState(false),
    [reason, setReason] = useState(""),
    [paying, setPaying] = useState(false),
    [acceptedNow, setAcceptedNow] = useState(false),
    [paymentNote, setPaymentNote] = useState<{
      variant: "info" | "danger";
      text: string;
    } | null>(null);
  const current = proposals.find(p => p.version === review.proposal_version);
  const open = useCallback(
    (selected = current) => {
      setShown({ ...selected, reviewVersion: review.version });
      setEditing(false);
      setTerms(selected.terms);
      setPrice((selected.terms.priceCents / 100).toFixed(2));
      setHours(String(selected.terms.turnaroundHours));
      setError("");
      setReason("");
      setConfirmed(false);
      setRequestKey(crypto.randomUUID());
    },
    [current, review.version]
  );
  const openedTarget = useRef("");
  useEffect(() => {
    const key = `${review.id}:${openTarget}`;
    if (!openTarget) {
      openedTarget.current = "";
      return;
    }
    if (current && openedTarget.current !== key) {
      openedTarget.current = key;
      open(proposals.find(p => String(p.version) === openTarget) || current);
    }
  }, [openTarget, review.id, current, open, proposals]);
  useEffect(() => {
    if (openRequest > 0 && current) open();
    // Only a new request opens the modal; later prop changes must not reopen it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);
  const returned = useRef(false);
  useEffect(() => {
    if (returned.current || attorney) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("payment");
    const cardOutcome = params.get("card");
    if (!outcome && !cardOutcome) return;
    returned.current = true;
    const sessionId = params.get("session_id");
    params.delete("payment");
    params.delete("card");
    params.delete("session_id");
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`
    );
    if (cardOutcome === "cancelled") {
      setPaymentNote({
        variant: "info",
        text: "No card was saved. Add one to be charged automatically once both sides agree.",
      });
      return;
    }
    if (cardOutcome === "saved" && sessionId) {
      reviewApi(`/${review.id}/payment/card/confirm`, "POST", { sessionId })
        .then(async result => {
          await onChanged();
          setPaymentNote({
            variant: result.payment === "failed" ? "danger" : "info",
            text:
              result.payment === "charged"
                ? "Card saved and charged. Your review has started."
                : result.payment === "failed"
                  ? "Your card was saved but could not be charged. Pay to start your review."
                  : review.proposal_state === "awaiting_client"
                    ? "Card saved. It will be charged automatically when you accept the revised proposal."
                    : "Card saved. It will be charged automatically when an attorney accepts.",
          });
        })
        .catch(e => setPaymentNote({ variant: "danger", text: e.message }));
      return;
    }
    if (outcome === "cancelled") {
      setPaymentNote({
        variant: "info",
        text: "Payment was not completed. Nothing was charged; you can pay when you are ready.",
      });
      return;
    }
    if (outcome !== "success" || !sessionId) return;
    reviewApi(`/${review.id}/payment/confirm`, "POST", { sessionId })
      .then(async result => {
        await onChanged();
        setPaymentNote(
          result.applied
            ? {
                variant: "info",
                text: "Payment received. Your review has started.",
              }
            : result.paid
              ? {
                  variant: "danger",
                  text: "Your payment arrived after this review changed, so it is being refunded in full.",
                }
              : {
                  variant: "info",
                  text: "Your payment is still processing. This page will update when it completes.",
                }
        );
      })
      .catch(e => setPaymentNote({ variant: "danger", text: e.message }));
  }, [attorney, review.id, review.proposal_state, onChanged]);
  if (!current || review.proposal_state === "legacy") return null;
  const agreed = review.proposal_state === "agreed";
  const charged = ["unpaid", "awaiting_payment", "paid", "refunding", "refunded"].includes(
    review.payment_state
  );
  const awaitingPayment = agreed && review.payment_state === "awaiting_payment";
  const lastPayment = payments[payments.length - 1];
  const failedCharge =
    awaitingPayment &&
    lastPayment?.status === "failed" &&
    lastPayment.proposal_version === review.proposal_version
      ? lastPayment
      : null;
  const cardLabel = savedCard
    ? `${savedCard.brand.replace(/^\w/, c => c.toUpperCase())} •••• ${savedCard.last4}`
    : "";
  const needsCard =
    !attorney &&
    review.payment_state === "unpaid" &&
    current.terms.priceCents > 0 &&
    !review.closed_at &&
    review.status !== "cancelled";
  async function saveCard() {
    if (paying) return;
    setPaying(true);
    setPaymentNote(null);
    try {
      const { url } = await reviewApi<{ url: string }>(
        `/${review.id}/payment/card`,
        "POST",
        {}
      );
      window.location.assign(url);
    } catch (e: any) {
      setPaying(false);
      setPaymentNote({ variant: "danger", text: e.message });
    }
  }
  async function pay() {
    if (paying) return;
    setPaying(true);
    setPaymentNote(null);
    try {
      const { url } = await reviewApi<{ url: string }>(
        `/${review.id}/payment/checkout`,
        "POST",
        { proposalVersion: review.proposal_version }
      );
      window.location.assign(url);
    } catch (e: any) {
      setPaying(false);
      setPaymentNote({ variant: "danger", text: e.message });
      if (
        e instanceof ReviewApiError &&
        [401, 403, 404, 503].includes(e.status)
      )
        onAccessError(e);
    }
  }
  const stale =
    shown &&
    (shown.reviewVersion !== review.version ||
      shown.version !== review.proposal_version);
  const eligible = review.attorney_status === "approved";
  const canAccept =
    !stale &&
    eligible &&
    review.status === "assigned" &&
    !awaitingPayment &&
    (attorney
      ? ["awaiting_attorney", "agreed"].includes(review.proposal_state)
      : review.proposal_state === "awaiting_client");
  const canRevise =
    attorney &&
    !stale &&
    review.status === "assigned" &&
    [
      "awaiting_attorney",
      "awaiting_client",
      "declined",
      "needs_revision",
    ].includes(review.proposal_state);
  const previous =
    shown && [...proposals].reverse().find(p => p.version < shown.version);
  function editTerms(update: Partial<ProposalTermsData>) {
    setTerms(old => (old ? { ...old, ...update } : old));
    setRequestKey(crypto.randomUUID());
  }
  async function act(action: "accept" | "decline" | "revise") {
    if (!access || busy || stale) return;
    if (
      action === "revise" &&
      (!terms ||
        !terms.scope.trim() ||
        !/^\d+(\.\d{1,2})?$/.test(price) ||
        Number(price) > 1000000 ||
        !Number.isInteger(Number(hours)) ||
        Number(hours) < 1 ||
        Number(hours) > 2160)
    ) {
      setError(
        "Enter a scope, a nonnegative USD price with up to two decimals, and 1–2,160 whole hours."
      );
      return;
    }
    if (action === "revise" && !reason.trim()) {
      setError("Tell the client why you are proposing these changes.");
      return;
    }
    if(betaMode && attorney && action !== "decline" && !confirmed) { setError("Confirm your conflict check before continuing."); return; }
    setBusy(true);
    setError("");
    try {
      await reviewApi(`/${review.id}/proposals/${action}`, "POST", {
        version: shown.reviewVersion,
        proposalVersion: shown.version,
        ...(betaMode && attorney && action !== "decline" ? { conflictConfirmed: true } : {}),
        ...(action === "revise"
          ? {
              requestKey,
              ...(reason.trim() ? { changeReason: reason.trim() } : {}),
              terms: {
                ...terms,
                priceCents: Math.round(Number(price) * 100),
                turnaroundHours: Number(hours),
              },
            }
          : {}),
      });
      await onChanged();
      setShown(null);
      if (action === "accept" && attorney) setAcceptedNow(true);
    } catch (e: any) {
      setError(e.message);
      if (
        e instanceof ReviewApiError &&
        [401, 403, 404, 503].includes(e.status)
      )
        onAccessError(e);
    } finally {
      setBusy(false);
    }
  }
  const stateText = awaitingPayment
    ? attorney
      ? "Agreed · waiting for the client's payment"
      : "Agreed · pay to start your review"
    : agreed
      ? "Agreed review"
      : !attorney
        ? review.proposal_state === "awaiting_client"
          ? "Your attorney suggested changes"
          : review.proposal_state === "declined"
            ? "You declined the changes · waiting for new terms"
            : review.proposal_state === "needs_revision"
              ? "New attorney matched · terms being updated"
              : "You approved · waiting for an attorney to accept"
        : review.proposal_state === "awaiting_client"
          ? "Revised proposal · client approval needed"
          : review.proposal_state === "declined"
            ? "Changes declined · awaiting a new proposal"
            : review.proposal_state === "needs_revision"
              ? "New attorney assigned · proposal needs updating"
              : "Client-approved proposal · awaiting attorney acceptance";
  // Deadline if the attorney accepted this proposal right now (display only).
  const previewDue = (turnaroundHours: number) =>
    dateLabel(new Date(Date.now() + turnaroundHours * 3_600_000).toISOString());
  const acceptedText = review.due_at
    ? `Accepted. ${review.payment_state === "paid" ? `The client was charged ${proposalPrice(current.terms)}. ` : ""}Your answer is due ${dueLabel(review.due_at)}.`
    : "Accepted. Waiting for the client's payment; the deadline starts when it clears.";
  return (
    <>
      {!hideBar && (
        <section
          className={`review-proposal-bar ${agreed ? "is-agreed" : ""}${compact ? " is-compact" : ""}`}
          aria-label="Review proposal"
        >
          <div>
            <strong>{stateText}</strong>
            <span>
              {proposalPrice(current.terms)}
              {charged ? "" : " (beta test, not charged)"} ·{" "}
              {current.terms.turnaroundHours} hours
              {review.completed_at
                ? ` · Delivered ${dateLabel(review.completed_at)}`
                : review.due_at
                  ? ` · Due ${dateLabel(review.due_at)}`
                  : charged && current.terms.priceCents > 0
                    ? " from payment"
                    : " from agreement"}
              {review.payment_state === "paid" ? " · Paid" : review.payment_state==="refunding" ? " · Refund pending" : review.payment_state==="refunded" ? " · Refunded" : ""}
            </span>
          </div>
          <div className="review-proposal-actions">
            {!(
              customerBanner &&
              !attorney &&
              !agreed &&
              review.proposal_state === "awaiting_client"
            ) && (
              <Button
                variant={
                  agreed ||
                  (!attorney && review.proposal_state !== "awaiting_client")
                    ? "outline-dark"
                    : "primary"
                }
                onClick={() => open()}
              >
                {agreed
                  ? "View agreement"
                  : !attorney && review.proposal_state === "awaiting_client"
                    ? "Review changes"
                    : attorney
                      ? "Review proposal"
                      : "View terms"}
              </Button>
            )}
            {awaitingPayment && !attorney && (
              <Button disabled={paying} onClick={pay}>
                {paying
                  ? "Opening secure checkout…"
                  : `Pay ${proposalPrice(current.terms)}`}
              </Button>
            )}
          </div>
        </section>
      )}
      {needsCard && (
        <section className="review-card-on-file" aria-label="Payment card">
          <span>
            {savedCard
              ? `${cardLabel} will be charged ${proposalPrice(current.terms)} automatically ${review.proposal_state === "awaiting_client" ? "when you accept the revised proposal" : "when an attorney accepts"}.`
              : review.proposal_state === "awaiting_client"
                ? "Add a card for the revised proposal. Payment is collected only once both sides agree."
                : "Add a card so your review starts as soon as an attorney accepts. Nothing is charged until then."}
          </span>
          <Button
            size="sm"
            variant={savedCard ? "link" : "primary"}
            disabled={paying}
            onClick={saveCard}
          >
            {paying ? "Opening…" : savedCard ? "Replace card" : "Add card"}
          </Button>
        </section>
      )}
      {failedCharge && !attorney && (
        <Alert variant="warning">
          We couldn’t charge {cardLabel || "your saved card"}
          {failedCharge.failure_message
            ? `: ${failedCharge.failure_message}`
            : "."}{" "}
          Pay with another card to start your review.
        </Alert>
      )}
      {acceptedNow && attorney && review.status!=="completed" && !review.closed_at && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setAcceptedNow(false)}
        >
          {acceptedText}
        </Alert>
      )}
      {paymentNote && (
        <Alert
          variant={paymentNote.variant}
          dismissible
          onClose={() => setPaymentNote(null)}
        >
          {paymentNote.text}
        </Alert>
      )}
      <Modal
        show={!!shown}
        onHide={() => !busy && setShown(null)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>
            {editing
              ? "Propose changes"
              : agreed
                ? "Review agreement"
                : attorney
                  ? "Review proposal"
                  : "Price and terms"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {stale && (
            <Alert variant="warning">
              The review changed while this proposal was open.{" "}
              <Button variant="link" onClick={() => open()}>
                Read the latest proposal
              </Button>
            </Alert>
          )}
          {shown && (
            <>
              {attorney && (
                <dl className="proposal-client">
                  <div>
                    <dt>Client</dt>
                    <dd>{review.client_name}</dd>
                  </div>
                  <div>
                    <dt>Practice</dt>
                    <dd>{topicLabel(review.topic_key)}</dd>
                  </div>
                  <div>
                    <dt>Jurisdiction</dt>
                    <dd>
                      {jurisdictionLabel(
                        review.assigned_coverage?.jurisdiction ||
                          review.jurisdiction
                      )}
                    </dd>
                  </div>
                  {review.created_at && (
                    <div>
                      <dt>Submitted</dt>
                      <dd>{shortDate(review.created_at)}</dd>
                    </div>
                  )}
                </dl>
              )}
              <p className="proposal-demo-note">
                Proposal {shown.version} ·{" "}
                {charged && shown.terms.priceCents > 0
                  ? "Payment is collected by card once both sides agree."
                  : charged
                    ? "No payment is required for this proposal."
                    : "Beta test — no charge will be made."}
              </p>
              {editing && terms ? (
                <>
                  <Form.Group controlId="proposal-scope">
                    <Form.Label>Scope and deliverable</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      maxLength={8000}
                      value={terms.scope}
                      disabled={busy}
                      onChange={e => editTerms({ scope: e.target.value })}
                    />
                  </Form.Group>
                  <div className="proposal-fields">
                    <Form.Group controlId="proposal-price">
                      <Form.Label>
                        Fixed fee (USD)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="1000000"
                        step="0.01"
                        value={price}
                        disabled={busy || betaMode}
                        onChange={e => {
                          setPrice(e.target.value);
                          setRequestKey(crypto.randomUUID());
                        }}
                      />
                    </Form.Group>
                    <Form.Group controlId="proposal-hours">
                      <Form.Label>Turnaround (elapsed hours)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="2160"
                        step="1"
                        value={hours}
                        disabled={busy || betaMode}
                        onChange={e => {
                          setHours(e.target.value);
                          setRequestKey(crypto.randomUUID());
                        }}
                      />
                      {Number(hours) >= 1 && (
                        <Form.Text>
                          If the client approves now: due{" "}
                          {previewDue(Number(hours))}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </div>
                  <div className="proposal-fields">
                    <Form.Group controlId="proposal-rounds">
                      <Form.Label>Included clarification rounds</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={20}
                        disabled={busy || betaMode}
                        value={terms.clarificationRounds ?? ""}
                        placeholder="Keep original terms"
                        onChange={e =>
                          editTerms({
                            clarificationRounds:
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                    <Form.Group controlId="proposal-days">
                      <Form.Label>
                        Clarification window (calendar days)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={90}
                        disabled={busy || betaMode}
                        value={terms.clarificationDays ?? ""}
                        placeholder="Keep original terms"
                        onChange={e =>
                          editTerms({
                            clarificationDays:
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </div>
                  <Form.Group controlId="proposal-reason">
                    <Form.Label>Message to the client (required)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={2000}
                      value={reason}
                      disabled={busy}
                      onChange={e => {
                        setReason(e.target.value);
                        setRequestKey(crypto.randomUUID());
                      }}
                      placeholder="Explain what you changed and why."
                    />
                  </Form.Group>
                  {terms.additionalTerms && (
                    <div className="proposal-fixed-terms">
                      <strong>Platform terms (not editable)</strong>
                      <p>{terms.additionalTerms}</p>
                    </div>
                  )}
                  <p className="mt-3">
                    Sending confirms your acceptance of these terms. Work starts
                    only after the client accepts this version. The client can
                    discuss or decline the changes.
                  </p>
                </>
              ) : (
                <>
                  {previous && (
                    <section
                      className="proposal-changes"
                      aria-label="Changes from previous proposal"
                    >
                      <h3>What changed</h3>
                      {previous.terms.priceCents !== shown.terms.priceCents && (
                        <p>
                          Price: {proposalPrice(previous.terms)} →{" "}
                          {proposalPrice(shown.terms)}
                        </p>
                      )}
                      {previous.terms.turnaroundHours !==
                        shown.terms.turnaroundHours && (
                        <p>
                          Turnaround: {previous.terms.turnaroundHours} →{" "}
                          {shown.terms.turnaroundHours} hours
                        </p>
                      )}
                      {shown.change_reason && (
                        <p>
                          <strong>Attorney’s explanation:</strong>{" "}
                          {shown.change_reason}
                        </p>
                      )}
                      {[
                        ["scope", "Scope and deliverable"],
                        ["additionalTerms", "Additional terms"],
                      ].map(
                        ([key, label]) =>
                          previous.terms[key] !== shown.terms[key] && (
                            <div key={key}>
                              <h4 className="h6">{label} changes</h4>
                              <p className="proposal-before-after">
                                <span className="proposal-before">
                                  <small>Before</small>
                                  {previous.terms[key] || "—"}
                                </span>
                                <span className="proposal-after">
                                  <small>Now</small>
                                  {shown.terms[key] || "—"}
                                </span>
                              </p>
                            </div>
                          )
                      )}
                      {(previous.terms.clarificationRounds !==
                        shown.terms.clarificationRounds ||
                        previous.terms.clarificationDays !==
                          shown.terms.clarificationDays) && (
                        <p>
                          <strong>Clarification allowance changed:</strong>{" "}
                          {previous.terms.clarificationRounds ??
                            "Original terms"}{" "}
                          rounds /{" "}
                          {previous.terms.clarificationDays ?? "original"} days
                          →{" "}
                          {shown.terms.clarificationRounds ?? "Original terms"}{" "}
                          rounds / {shown.terms.clarificationDays ?? "original"}{" "}
                          days.
                        </p>
                      )}
                    </section>
                  )}
                  <ProposalTerms
                    terms={shown.terms}
                    dueAt={review.due_at}
                    charged={charged}
                    attorney={attorney}
                  />
                  {!agreed && (
                    <p className="proposal-next-step">
                      {canAccept
                        ? charged && shown.terms.priceCents > 0
                          ? attorney
                            ? `Accepting charges the client ${proposalPrice(shown.terms)}. If payment clears now, your answer is due ${previewDue(shown.terms.turnaroundHours)}.`
                            : savedCard && review.payment_state === "unpaid"
                              ? `Accepting charges ${cardLabel} ${proposalPrice(shown.terms)} now. Work and the ${shown.terms.turnaroundHours}-hour answer clock start after payment succeeds. Reviewed is operated by Norix Legal LLC, the merchant shown at checkout.`
                              : `Accept these terms, then pay ${proposalPrice(shown.terms)} by card to start. Work and the answer clock begin after payment succeeds. Reviewed is operated by Norix Legal LLC.`
                          : attorney
                            ? `Accepting starts the review now. Your answer would be due ${previewDue(shown.terms.turnaroundHours)}.`
                            : "Accepting confirms these exact terms and starts the review and delivery clock."
                        : review.proposal_state === "awaiting_client"
                          ? "Work is paused until the client accepts the revised proposal."
                          : "Review work begins once both sides agree. Discussion is available to clarify the scope first."}
                    </p>
                  )}
                  {canAccept && (
                    <Form.Check
                      id="accept-review-proposal"
                      checked={confirmed}
                      disabled={busy}
                      onChange={e => setConfirmed(e.target.checked)}
                      label={
                        attorney
                          ? `I've checked for conflicts with ${parties || review.client_name || "this client"} and accept this scope, ${charged ? "" : ""}price, and deadline.`
                          : charged && shown.terms.priceCents > 0 && savedCard
                            ? `I agree to these terms and authorize the ${proposalPrice(shown.terms)} charge to ${cardLabel}.`
                            : `I agree to this scope, ${charged ? "" : ""}price, and turnaround.`
                      }
                    />
                  )}
                </>
              )}
              <details className="proposal-history">
                <summary>Proposal and approval history</summary>
                {proposals.map(p => (
                  <details key={p.version}>
                    <summary>
                      Proposal {p.version} ·{" "}
                      {p.origin === "platform"
                        ? "Platform terms"
                        : p.author_name}{" "}
                      · {dateLabel(p.created_at)}
                    </summary>
                    <ProposalTerms
                      terms={p.terms}
                      charged={charged}
                      attorney={attorney}
                    />
                    {decisions
                      .filter(d => d.proposal_version === p.version)
                      .map((d, i) => (
                        <p key={i}>
                          {d.actor_name} ·{" "}
                          {d.decision === "accept" ? "Accepted" : "Declined"} ·{" "}
                          {dateLabel(d.created_at)}
                        </p>
                      ))}
                  </details>
                ))}
              </details>
            </>
          )}
        </Modal.Body>
        {editing && betaMode && attorney && <Form.Check className="mx-3 mb-3" id="revision-conflicts" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} label={`I have checked the disclosed parties for conflicts (${parties || review.client_name || "this client"}) and accept this proposed scope.`} />}
        <Modal.Footer>
          <Button
            variant="light"
            disabled={busy}
            onClick={() => (editing ? setEditing(false) : setShown(null))}
          >
            {editing ? "Back to proposal" : "Close"}
          </Button>
          {!editing && !attorney && !stale && awaitingPayment && eligible && (
            <Button disabled={paying || busy} onClick={pay}>
              {paying
                ? "Opening secure checkout…"
                : `Pay ${proposalPrice(current.terms)}`}
            </Button>
          )}
          {!editing && !stale && needsCard && !savedCard && (
            <Button disabled={paying || busy} onClick={saveCard}>
              {paying ? "Opening secure checkout…" : "Add card — no charge now"}
            </Button>
          )}
          {editing ? (
            <Button disabled={!access || busy || stale || (betaMode && !confirmed)} onClick={() => act("revise")}>
              {busy ? "Sending…" : "Send revised proposal"}
            </Button>
          ) : (
            <>
              {canRevise && (
                <Button
                  variant="outline-dark"
                  disabled={busy}
                  onClick={() => setEditing(true)}
                >
                  Propose changes
                </Button>
              )}
              {canAccept && !attorney && (
                <Button
                  variant="outline-danger"
                  disabled={busy}
                  onClick={() => act("decline")}
                >
                  Decline changes — no charge
                </Button>
              )}
              {canAccept && (
                <Button
                  disabled={!access || busy || !confirmed}
                  onClick={() => act("accept")}
                >
                  {busy
                    ? "Confirming…"
                    : attorney
                      ? agreed
                        ? "Resume agreed review"
                        : // The footer renders before the modal opens, when nothing is shown yet.
                          charged && (shown ?? current).terms.priceCents > 0
                          ? `Accept — charge client ${proposalPrice((shown ?? current).terms)}`
                          : "Accept and start"
                      : charged && (shown ?? current).terms.priceCents > 0
                        ? savedCard
                          ? `Accept and pay ${proposalPrice((shown ?? current).terms)}`
                          : "Accept and continue to payment"
                        : "Accept and start review"}
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
